/**
 * Dashboard – main members area showing token balance, usage summary, recent activity.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Coins,
  TrendingUp,
  ShoppingCart,
  Activity,
  ArrowRight,
  BarChart3,
  Zap,
  Settings,
  RotateCcw,
  ShieldOff,
  RefreshCw,
} from 'lucide-react';
import {
  getTokenBalance,
  getUsageSummary,
  getUsageByApp,
  getTransactions,
} from '../services';
import type { TokenBalance, UsageSummary, UsageByApp, Transaction } from '../types';

export default function DashboardPage() {
  const { user } = useAuth();
  const [balance, setBalance] = useState<TokenBalance | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [appUsage, setAppUsage] = useState<UsageByApp[]>([]);
  const [recentTxns, setRecentTxns] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const [balRes, usageRes, appRes, txnRes] = await Promise.all([
        getTokenBalance(),
        getUsageSummary(),
        getUsageByApp(),
        getTransactions(),
      ]);
      if (balRes.data) setBalance(balRes.data);
      if (usageRes.data) setUsage(usageRes.data);
      if (appRes.data) setAppUsage(appRes.data);
      if (txnRes.data) setRecentTxns(txnRes.data.slice(0, 5));
    } catch (err) {
      console.error('Failed to load dashboard data', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { loadData(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-screen">
          <div className="spinner" />
          <p>Loading dashboard…</p>
        </div>
      </div>
    );
  }

  const balancePercent = balance
    ? Math.round((balance.available / (balance.lifetimePurchased || 1)) * 100)
    : 0;

  return (
    <div className="page-container">
      <div className="container">
        {/* ── Header ─────────────────────────────────────── */}
        <div className="page-header">
          <div>
            <h1>Dashboard</h1>
            <p>Welcome back, {user?.name}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              className="btn btn-ghost"
              onClick={() => loadData(true)}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            <Link to="/purchase" className="btn btn-primary">
              <ShoppingCart size={18} />
              Get Tokens
            </Link>
          </div>
        </div>

        {/* ── Balance Card ───────────────────────────────── */}
        <div className="balance-card">
          <div className="balance-card-inner">
            <div className="balance-main">
              <div className="balance-label">Available AI Tokens</div>
              <div className="balance-amount">
                <Coins size={32} />
                {balance?.available.toLocaleString()}
              </div>
              <div className="balance-bar-wrapper">
                <div className="balance-bar">
                  <div
                    className="balance-bar-fill"
                    style={{ width: `${balancePercent}%` }}
                  />
                </div>
                <span className="balance-bar-label">
                  {balancePercent}% remaining of {balance?.lifetimePurchased.toLocaleString()} purchased
                </span>
              </div>
            </div>
            <div className="balance-side">
              <div className="balance-stat">
                <TrendingUp size={16} />
                <span>Lifetime Used</span>
                <strong>{balance?.lifetimeUsed.toLocaleString()}</strong>
              </div>
              <div className="user-token-display">
                <label>Your User Token</label>
                <Link to="/settings" className="token-settings-link">
                  <Settings size={14} />
                  View in Settings
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats Row ──────────────────────────────────── */}
        <div className="stats-grid">
          <div className="stat-card">
            <Activity size={20} />
            <div className="stat-card-body">
              <span>Today</span>
              <strong>{usage?.today.toLocaleString()} tokens</strong>
            </div>
          </div>
          <div className="stat-card">
            <BarChart3 size={20} />
            <div className="stat-card-body">
              <span>This Week</span>
              <strong>{usage?.thisWeek.toLocaleString()} tokens</strong>
            </div>
          </div>
          <div className="stat-card">
            <TrendingUp size={20} />
            <div className="stat-card-body">
              <span>This Month</span>
              <strong>{usage?.thisMonth.toLocaleString()} tokens</strong>
            </div>
          </div>
          <div className="stat-card">
            <Zap size={20} />
            <div className="stat-card-body">
              <span>Avg / Day</span>
              <strong>{usage?.averagePerDay.toLocaleString()} tokens</strong>
            </div>
          </div>
        </div>

        <div className="dashboard-grid">
          {/* ── Usage by App ───────────────────────────────── */}
          <div className="card">
            <div className="card-header">
              <h3>Usage by Company/App</h3>
            </div>
            <div className="card-body">
              {appUsage.length === 0 ? (
                <p className="empty-state">No usage data yet.</p>
              ) : (
                <div className="app-usage-list">
                  {appUsage.map((app) => {
                    const totalUsed = appUsage.reduce((s, a) => s + a.tokensUsed, 0);
                    const pct = Math.round((app.tokensUsed / totalUsed) * 100);
                    return (
                      <div key={app.appName} className="app-usage-item">
                        <div className="app-usage-header">
                          <span className="app-name">{app.appName}</span>
                          <span className="app-tokens">
                            {app.tokensUsed.toLocaleString()} tokens
                          </span>
                        </div>
                        <div className="app-usage-bar">
                          <div className="app-usage-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="app-requests">{app.requestCount} requests</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Recent Transactions ────────────────────────── */}
          <div className="card">
            <div className="card-header">
              <h3>Recent Activity</h3>
              <Link to="/transactions" className="link-sm">
                View All <ArrowRight size={14} />
              </Link>
            </div>
            <div className="card-body">
              {recentTxns.length === 0 ? (
                <p className="empty-state">No transactions yet.</p>
              ) : (
                <div className="txn-list">
                  {recentTxns.map((txn) => (
                    <div key={txn.id} className="txn-item">
                      <div className={`txn-icon ${txn.type}`}>
                        {txn.type === 'purchase' && <ShoppingCart size={14} />}
                        {txn.type === 'usage' && <Activity size={14} />}
                        {txn.type === 'refund' && <Coins size={14} />}
                        {txn.type === 'auto-renew' && <RefreshCw size={14} />}
                        {txn.type === 'rotation' && <RotateCcw size={14} />}
                        {txn.type === 'revoke' && <ShieldOff size={14} />}
                      </div>
                      <div className="txn-details">
                        <span className="txn-desc">{txn.description}</span>
                        <span className="txn-meta">
                          {txn.appName && `${txn.appName} · `}
                          {new Date(txn.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className={`txn-amount ${txn.tokens > 0 ? 'positive' : txn.tokens === 0 ? 'neutral' : 'negative'}`}>
                        {txn.tokens === 0 ? '—' : `${txn.tokens > 0 ? '+' : ''}${txn.tokens.toLocaleString()}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
